import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime } from 'rxjs';

import {
  DifficultyLevelValue,
  QuestionOriginValue,
  QuestionSearchCriteria,
  QuestionStatusValue,
  QuestionTypeValue,
} from '../../../../../core/interfaces/question-search';
import { SubjectChoiceService } from '../../../../../core/services/utils/subject-choice.service';

/** Parcours tel qu'affiché dans la facette « parcours ». */
export interface QuestionFilterLearningPath {
  id: number;
  title: string;
}

/** Matière telle qu'affichée dans la facette « matière ». */
export interface QuestionFilterSubject {
  id: number;
  name: string;
  /** Parcours d'appartenance : deux parcours peuvent porter la même matière. */
  learningPathId: number;
  /** Titre du parcours, pour lever l'ambiguïté d'un nom de matière répété. */
  learningPathTitle: string;
}

/** Thème rattaché à une matière, pour cascader les deux facettes. */
export interface QuestionFilterTheme {
  id: number;
  title: string;
  subjectId: number;
}

/**
 * Facettes de la banque de questions.
 *
 * Composant présentationnel : il ne connaît ni le service de recherche ni la
 * pagination. Il reçoit les critères courants et réémet une copie modifiée ;
 * c'est au conteneur de décider quoi en faire (remise à la page 1, requête…).
 */
@Component({
  selector: 'app-question-filters',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './question-filters.component.html',
  styleUrl: './question-filters.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionFiltersComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly subjectChoice = inject(SubjectChoiceService);

  private readonly criteria = signal<QuestionSearchCriteria>({});

  /**
   * Dernier texte émis vers le parent. Sert à distinguer « le parent a changé
   * le texte » (réinitialisation → on resynchronise le champ) de « le parent
   * nous renvoie ce qu'on vient d'émettre » (frappe en cours → on ne touche à
   * rien, sinon le curseur saute).
   */
  private lastEmittedText = '';

  @Input({ required: true })
  set value(v: QuestionSearchCriteria) {
    this.criteria.set(v ?? {});
    const incoming = v?.text ?? '';
    if (incoming !== this.lastEmittedText) {
      this.lastEmittedText = incoming;
      this.textDraft.set(incoming);
    }
  }
  get value(): QuestionSearchCriteria {
    return this.criteria();
  }

  /** Parcours disponibles. Vide si le référentiel n'a pas pu être chargé. */
  readonly learningPaths = input<QuestionFilterLearningPath[]>([]);

  /** Matières disponibles. Vide si le référentiel n'a pas pu être chargé. */
  readonly subjects = input<QuestionFilterSubject[]>([]);

  /** Thèmes disponibles, filtrés sur la matière choisie. */
  readonly themes = input<QuestionFilterTheme[]>([]);

  /**
   * Le référentiel (matières/thèmes) est en erreur : on désactive les deux
   * facettes concernées plutôt que de proposer une liste inventée.
   */
  readonly taxonomyErrored = input(false);

  @Output() valueChange = new EventEmitter<QuestionSearchCriteria>();

  /** Demande de rechargement du référentiel matières/thèmes. */
  @Output() reloadTaxonomy = new EventEmitter<void>();

  /** Texte tapé, avant débounce. */
  readonly textDraft = signal('');

  private readonly textInput$ = new Subject<string>();

  readonly questionTypes: QuestionTypeValue[] = [
    'single_choice',
    'multiple_choice',
    'true_false',
    'short_text',
    'numeric',
    'matching',
    'ordering',
    'cloze',
  ];

  readonly difficultyLevels: DifficultyLevelValue[] = ['easy', 'medium', 'hard'];

  readonly statuses: QuestionStatusValue[] = [
    'draft',
    'pending_review',
    'approved',
    'rejected',
    'archived',
  ];

  readonly origins: QuestionOriginValue[] = [
    'manual',
    'imported',
    'ai_generated',
  ];

  /** Langues gérées par l'interface ; l'arabe est la langue par défaut. */
  readonly languages = ['ar', 'en', 'fr'];

  /** Matières proposées : restreintes au parcours choisi quand il y en a un. */
  readonly visibleSubjects = computed(() => {
    const learningPathId = this.criteria().learningPathId;
    const subjects = this.subjects();
    if (learningPathId == null) return subjects;
    return subjects.filter((s) => s.learningPathId === learningPathId);
  });

  /**
   * Libellés des matières visibles, dans leur ordre.
   *
   * <p>« Mathématiques » peut exister dans trois parcours : sans le parcours en
   * suffixe, la liste affiche trois options identiques et le choix se fait au
   * hasard. La règle est partagée avec les autres listes de matières de
   * l'application — trois copies auraient fini par diverger.</p>
   */
  private readonly subjectLabels = computed(() =>
    this.subjectChoice.disambiguate(
      this.visibleSubjects().map((s) => ({
        name: s.name,
        pathLabel: s.learningPathTitle,
      })),
    ),
  );

  /** Thèmes proposés : restreints à la matière choisie, sinon au parcours choisi. */
  readonly visibleThemes = computed(() => {
    const subjectId = this.criteria().subjectId;
    const themes = this.themes();
    if (subjectId != null) return themes.filter((t) => t.subjectId === subjectId);

    const learningPathId = this.criteria().learningPathId;
    if (learningPathId == null) return themes;
    const scoped = new Set(this.visibleSubjects().map((s) => s.id));
    return themes.filter((t) => scoped.has(t.subjectId));
  });

  readonly activeCount = computed(() => {
    const c = this.criteria();
    let n = 0;
    if (c.learningPathId != null) n++;
    if (c.subjectId != null) n++;
    if (c.themeId != null) n++;
    if (c.questionType) n++;
    if (c.difficultyLevel) n++;
    if (c.status) n++;
    if (c.origin) n++;
    if (c.language) n++;
    if ((c.text ?? '').trim()) n++;
    if (c.includeArchived) n++;
    return n;
  });

  readonly hasActiveFilters = computed(() => this.activeCount() > 0);

  constructor() {
    // Sans débounce, chaque frappe partirait au serveur : une recherche de
    // huit caractères déclencherait huit requêtes concurrentes.
    // Pas de `distinctUntilChanged` ici : après une réinitialisation, retaper le
    // même texte qu'avant doit relancer la recherche, or l'opérateur le
    // filtrerait comme un doublon et la liste resterait figée.
    this.textInput$
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        // Une frappe encore en attente au moment de la remise à zéro est
        // périmée : l'émettre rétablirait une recherche que le champ, vidé,
        // ne montre plus.
        if (text !== this.textDraft()) return;
        this.lastEmittedText = text;
        this.emit({ text: text.trim() ? text : undefined });
      });
  }

  onText(raw: string): void {
    this.textDraft.set(raw);
    this.textInput$.next(raw);
  }

  /** Libellé d'une matière, suffixé du parcours quand le nom est ambigu. */
  subjectLabel(subject: QuestionFilterSubject): string {
    const index = this.visibleSubjects().indexOf(subject);
    return index >= 0 ? this.subjectLabels()[index] : subject.name;
  }

  onLearningPath(raw: string): void {
    const learningPathId = raw ? Number(raw) : undefined;
    // La matière et le thème retenus appartiennent à l'ancien parcours : les
    // garder croiserait deux portées incompatibles et ne renverrait rien.
    this.emit({ learningPathId, subjectId: undefined, themeId: undefined });
  }

  onSubject(raw: string): void {
    const subjectId = raw ? Number(raw) : undefined;
    // Le thème choisi n'appartient probablement plus à la nouvelle matière :
    // le conserver produirait un croisement vide et incompréhensible.
    this.emit({ subjectId, themeId: undefined });
  }

  onTheme(raw: string): void {
    this.emit({ themeId: raw ? Number(raw) : undefined });
  }

  onType(raw: string): void {
    this.emit({ questionType: (raw || undefined) as QuestionTypeValue | undefined });
  }

  onDifficulty(raw: string): void {
    this.emit({
      difficultyLevel: (raw || undefined) as DifficultyLevelValue | undefined,
    });
  }

  /**
   * Le statut « archivée » emporte l'inclusion des archives.
   *
   * <p>Le serveur écarte les questions archivées tant qu'on ne les demande
   * pas. Choisir ce statut sans cocher la case posait donc deux conditions
   * contraires — « archivée » et « pas archivée » — et la liste restait vide,
   * quoi qu'il y ait en archive : une option morte.</p>
   */
  onStatus(raw: string): void {
    const status = (raw || undefined) as QuestionStatusValue | undefined;
    if (status === 'archived') {
      this.emit({ status, includeArchived: true });
      return;
    }
    // Quitter « archivée » retire l'inclusion qu'elle avait entraînée : la
    // laisser mêlerait des archives à une liste qui ne les demande plus.
    const leavesArchived = this.criteria().status === 'archived';
    this.emit(leavesArchived ? { status, includeArchived: undefined } : { status });
  }

  onOrigin(raw: string): void {
    this.emit({ origin: (raw || undefined) as QuestionOriginValue | undefined });
  }

  onLanguage(raw: string): void {
    this.emit({ language: raw || undefined });
  }

  onIncludeArchived(checked: boolean): void {
    // Décocher la case en gardant le statut « archivée » referait la même
    // contradiction dans l'autre sens : le statut tombe avec elle.
    const dropsArchivedStatus = !checked && this.criteria().status === 'archived';
    this.emit({
      includeArchived: checked ? true : undefined,
      ...(dropsArchivedStatus ? { status: undefined } : {}),
    });
  }

  reset(): void {
    this.lastEmittedText = '';
    this.textDraft.set('');
    const c = this.criteria();
    // La pagination et le tri ne sont pas des facettes : les réinitialiser
    // ferait sauter l'utilisateur d'un tri qu'il vient de choisir.
    this.valueChange.emit({
      pageSize: c.pageSize,
      sortBy: c.sortBy,
      sortDescending: c.sortDescending,
    });
  }

  labelKey(prefix: string, value: string): string {
    return `QUESTION_BANK.${prefix}.${value.toUpperCase()}`;
  }

  private emit(patch: Partial<QuestionSearchCriteria>): void {
    this.valueChange.emit({ ...this.criteria(), ...patch });
  }
}
